
import React, { useState, useEffect } from 'react';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Sparkles, Loader2, Tag } from 'lucide-react';
import { TabComponentProps } from './types';
import { useAIAssistant } from '@/contexts/ai-assistant/AIAssistantContext';
import { Dialog, DialogContent, DialogTrigger } from '@/components/ui/dialog';
import TitleGenerator from '../editor/ai-assistant/TitleGenerator';
import TagGenerator from '../editor/ai-assistant/TagGenerator';

interface ExtendedTabProps extends TabComponentProps {
  content?: string;
  title?: string;
}

const MetadataTab: React.FC<ExtendedTabProps> = ({ 
  metadata, 
  handleInputChange, 
  content = '',
  title = ''
}) => {
  const [tags, setTags] = useState<string[]>(metadata.tags || []);
  const [tagInput, setTagInput] = useState('');
  const [isTitleDialogOpen, setIsTitleDialogOpen] = useState(false);
  const [isTagDialogOpen, setIsTagDialogOpen] = useState(false);

  const { isProcessing, activeFeature } = useAIAssistant();

  // Sync tags with metadata when it changes
  useEffect(() => {
    if (metadata.tags) {
      setTags(metadata.tags);
    }
  }, [metadata.tags]);

  const handleAddTag = () => {
    if (tagInput.trim() && !tags.includes(tagInput.trim())) {
      const newTags = [...tags, tagInput.trim()];
      setTags(newTags);
      handleInputChange('tags', newTags);
      setTagInput('');
    }
  };

  const handleAddMultipleTags = (newTags: string[]) => {
    const uniqueTags = newTags.filter(tag => !tags.includes(tag));
    if (uniqueTags.length > 0) {
      const updatedTags = [...tags, ...uniqueTags];
      setTags(updatedTags);
      handleInputChange('tags', updatedTags);
    }
  };

  const handleRemoveTag = (tag: string) => {
    const newTags = tags.filter(t => t !== tag);
    setTags(newTags);
    handleInputChange('tags', newTags);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleAddTag();
    }
  };

  const handleTitleSelect = (title: string) => {
    handleInputChange('title', title);
    setIsTitleDialogOpen(false);
  };

  return (
    <div className="space-y-4 p-4">
      <div className="space-y-2">
        <div className="flex items-start justify-between">
          <Label htmlFor="excerpt">Excerpt</Label>
        </div>
        <Textarea 
          id="excerpt"
          placeholder="Brief summary of your article"
          className="resize-none"
          rows={3}
          value={metadata.excerpt || ''}
          onChange={(e) => handleInputChange('excerpt', e.target.value)}
        />
        <p className="text-xs text-muted-foreground">Will appear in article listings and social shares</p>
      </div>
      
      <div className="space-y-2">
        <div className="flex items-center justify-between">
          <Label htmlFor="tag-input">Tags</Label>
          <Dialog open={isTagDialogOpen} onOpenChange={setIsTagDialogOpen}>
            <DialogTrigger asChild>
              <Button 
                variant="outline" 
                size="sm"
                disabled={isProcessing || !content}
                className="flex items-center gap-1"
              >
                {activeFeature === 'generateTags' ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-1 animate-spin" />
                    Generating...
                  </>
                ) : (
                  <>
                    <Sparkles className="h-4 w-4 mr-1 text-yellow-500" />
                    Generate Tags
                  </>
                )}
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-3xl">
              <TagGenerator
                content={content}
                title={title}
                existingTags={tags}
                onAddTags={handleAddMultipleTags}
                onClose={() => setIsTagDialogOpen(false)}
              />
            </DialogContent>
          </Dialog>
        </div>
        <div className="flex space-x-2">
          <Input
            id="tag-input"
            placeholder="Add a tag"
            value={tagInput}
            onChange={(e) => setTagInput(e.target.value)}
            onKeyDown={handleKeyDown}
          />
          <Button type="button" onClick={handleAddTag} variant="outline">
            Add
          </Button>
        </div>
        {tags.length > 0 && (
          <div className="flex flex-wrap gap-2 mt-2">
            {tags.map(tag => (
              <div key={tag} className="bg-secondary text-secondary-foreground px-3 py-1 rounded-full text-sm flex items-center">
                {tag}
                <button 
                  onClick={() => handleRemoveTag(tag)} 
                  className="ml-2 text-muted-foreground hover:text-foreground"
                >
                  ×
                </button>
              </div>
            ))}
          </div>
        )}
      </div>
      
      <div className="space-y-2">
        <div className="flex items-center justify-between">
          <Label htmlFor="featured-image">Featured Image URL</Label>
          {/* AI image generation could be added here in the future */}
        </div>
        <Input
          id="featured-image"
          type="text"
          placeholder="https://example.com/image.jpg"
          value={metadata.featuredImage || ''}
          onChange={(e) => handleInputChange('featuredImage', e.target.value)}
        />
      </div>
      
      <div className="space-y-2">
        <Label htmlFor="featured-image-alt">Featured Image Alt Text</Label>
        <Input
          id="featured-image-alt"
          type="text"
          placeholder="Description of the image"
          value={metadata.featuredImageAlt || ''}
          onChange={(e) => handleInputChange('featuredImageAlt', e.target.value)}
        />
      </div>
      
      <div className="space-y-2">
        <Label htmlFor="read-time">Estimated Reading Time (minutes)</Label>
        <Input
          id="read-time"
          type="number"
          min="1"
          step="1"
          placeholder="5"
          value={metadata.readingTime || ''}
          onChange={(e) => handleInputChange('readingTime', parseInt(e.target.value) || '')}
        />
      </div>

      <Dialog open={isTitleDialogOpen} onOpenChange={setIsTitleDialogOpen}>
        <DialogContent className="max-w-3xl">
          <TitleGenerator
            content={content}
            currentTitle={metadata.title || ''}
            onSelectTitle={handleTitleSelect}
            onClose={() => setIsTitleDialogOpen(false)}
          />
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default MetadataTab;
